import { atom } from 'jotai';
import cloneDeep from 'lodash/cloneDeep';

import {
  ContentStep,
  Plan,
  PlanSection,
  Quiz,
  QuizDetail,
} from '@cfa/pathway-fe-shared/xp';

import Constants from '@/constants';

import { store } from '@/atoms';

/**
 * Union of Plan and Quiz fields needed by the shared translations modal.
 * Can't use either generated type alone — Plan has no quiz fields (`quizId`, `questions`)
 * and QuizDetail has no plan fields (`content.sections`, `content.steps`).
 * Callers cast back to their specific type in `onSave`.
 *
 * @todo Make the modal generic over the entity type so `onSave` returns the
 * caller's exact type (Plan or EditableQuizDetail) without casting.
 */
export type ManageTranslationsState = Partial<Plan> &
  Partial<Pick<QuizDetail, 'questions' | 'quizId'>>;

export type ManageTranslationsInput =
  | ManageTranslationsState
  | QuizDetail
  | Partial<Plan>
  | Quiz
  | undefined;

export const manageTranslationsMutableAtom = atom(
  {} as ManageTranslationsState,
);

interface UpdatePayload {
  id: string;
  value?: string;
  targetLanguage: string;
}

type UpdateTaskPayload = UpdatePayload & {
  sectionId?: string;
};

type UpdateTaskUrlPayload = UpdateTaskPayload & {
  index: number;
};

interface UpdateQuestionPayload {
  value?: string;
  questionIndex: number;
  targetLanguage: string;
}

interface UpdateAnswerPayload {
  value?: string;
  questionIndex: number;
  answerIndex: number;
  targetLanguage: string;
}

const initializeTrainingPlanTranslations = (
  payload: ManageTranslationsInput,
) => {
  const initializedState = initializeMissingProperties(payload);
  store.set(manageTranslationsMutableAtom, { ...initializedState });
};

const updatePlanNameTranslation = ({
  id,
  value,
  targetLanguage,
}: UpdatePayload) =>
  store.set(manageTranslationsMutableAtom, prev => {
    const mutable = { ...prev };
    if (mutable.name && mutable.id === id) {
      mutable.name[targetLanguage] = value ?? '';
    }
    return mutable;
  });

const updateDescriptionTranslation = ({
  id,
  value,
  targetLanguage,
}: UpdatePayload): void => {
  store.set(manageTranslationsMutableAtom, prev => {
    const mutable = { ...prev };
    if (mutable.description && mutable.id === id) {
      mutable.description[targetLanguage] = value ?? '';
    }
    return mutable;
  });
};

const updateSectionTranslation = ({
  id,
  value,
  targetLanguage,
}: UpdatePayload) =>
  store.set(manageTranslationsMutableAtom, prev => {
    const mutable = { ...prev };
    const section = filterSection(mutable.content?.sections, id);
    if (section?.name) {
      section.name[targetLanguage] = value ?? '';
    }
    return mutable;
  });

const updateTaskNameTranslation = ({
  id,
  value,
  sectionId,
  targetLanguage,
}: UpdateTaskPayload) =>
  store.set(manageTranslationsMutableAtom, prev => {
    const mutable = { ...prev };
    let steps = mutable.content?.steps;
    if (sectionId) {
      steps = filterSection(mutable.content?.sections, sectionId)?.steps;
    }
    const step = filterStep(steps, id);
    if (step?.name) {
      step.name[targetLanguage] = value ?? '';
    }
    return mutable;
  });

const updateTaskNotesTranslation = ({
  id,
  value,
  sectionId,
  targetLanguage,
}: UpdateTaskPayload) =>
  store.set(manageTranslationsMutableAtom, prev => {
    const mutable = { ...prev };
    let steps = mutable.content?.steps;
    if (sectionId) {
      steps = filterSection(mutable.content?.sections, sectionId)?.steps;
    }
    const step = filterStep(steps, id);
    if (step?.type === Constants.STEP_TYPES.TASK && step.note) {
      step.note[targetLanguage] = value ?? '';
    }
    return mutable;
  });

const updateTaskUrlTranslation = ({
  id,
  value,
  sectionId,
  index,
  targetLanguage,
}: UpdateTaskUrlPayload) =>
  store.set(manageTranslationsMutableAtom, prev => {
    const mutable = { ...prev };
    let steps = mutable.content?.steps;
    if (sectionId) {
      steps = filterSection(mutable.content?.sections, sectionId)?.steps;
    }
    const step = filterStep(steps, id);
    if (step?.type === Constants.STEP_TYPES.TASK && step.urls) {
      const url = step.urls.at(index)?.urlSet;
      if (url) {
        url[targetLanguage] = value ?? '';
      }
    }
    return mutable;
  });

const updateQuizNameTranslation = ({
  id,
  value,
  targetLanguage,
}: UpdatePayload) =>
  store.set(manageTranslationsMutableAtom, prev => {
    const mutable = { ...prev };
    if ('quizId' in mutable && mutable.quizId === id && mutable.name) {
      mutable.name[targetLanguage] = value ?? '';
    }
    return mutable;
  });

const updateQuestionTranslation = ({
  value,
  questionIndex,
  targetLanguage,
}: UpdateQuestionPayload) =>
  store.set(manageTranslationsMutableAtom, prev => {
    const mutable = { ...prev };
    const questions = mutable.questions;
    const questionDefinition = questions?.at(questionIndex);
    if (questionDefinition) {
      questionDefinition.question[targetLanguage] = value ?? '';
    }
    return mutable;
  });

const updateAnswerTranslation = ({
  value,
  questionIndex,
  answerIndex,
  targetLanguage,
}: UpdateAnswerPayload) =>
  store.set(manageTranslationsMutableAtom, prev => {
    const mutable = { ...prev };
    const answer = mutable.questions
      ?.at(questionIndex)
      ?.answers.at(answerIndex)?.answer;
    if (answer) {
      answer[targetLanguage] = value ?? '';
    }
    return mutable;
  });

export const actions = {
  initializeTrainingPlanTranslations,
  updatePlanNameTranslation,
  updateDescriptionTranslation,
  updateSectionTranslation,
  updateTaskNameTranslation,
  updateTaskNotesTranslation,
  updateTaskUrlTranslation,
  updateQuizNameTranslation,
  updateQuestionTranslation,
  updateAnswerTranslation,
};

export const initializeMissingProperties = (
  payload: ManageTranslationsInput,
): ManageTranslationsState => {
  if (!payload) {
    return {};
  }

  const initializedState = cloneDeep(payload) as ManageTranslationsState;

  if (!initializedState.name?.es) {
    if (initializedState.name) {
      initializedState.name.es = '';
    }
  }

  if (!initializedState.description?.es) {
    if (initializedState.description) {
      initializedState.description.es = '';
    }
  }

  initializedState.content?.steps?.map(step => {
    if (step.type !== 'task') {
      return;
    }

    step.name.es ||= '';
    if (step.note) {
      step.note.es ||= '';
    }
  });

  initializedState.content?.sections?.map(section => {
    section.name.es ||= '';

    section.steps?.map(step => {
      if (step.type === 'task') {
        step.name.es ||= '';
        if (step.note) {
          step.note.es ||= '';
        }
      }
    });
  });

  return initializedState;
};

const filterSection = (
  sections: PlanSection[] | undefined,
  sectionId: string,
) => sections?.find(section => section.id === sectionId);

const filterStep = (steps: ContentStep[] | undefined, id: string) =>
  steps?.find(step => step.id === id);
